import { ScriptDataAttributes } from './data-attributes'
import { selfScriptEl } from './script-attributes'

/**
 * Which customer IAM stack to use for North America parallel B2C + Entra External ID.
 * Default remains B2C until integrators opt in via `data-auth-identity-provider`.
 */
export const IDENTITY_PROVIDER_B2C = 'b2c' as const
export const IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID = 'entra-external-id' as const

export type IdentityProvider = typeof IDENTITY_PROVIDER_B2C | typeof IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID

export function parseIdentityProviderAttribute(value: string | null | undefined): IdentityProvider {
	if (value?.trim().toLowerCase() === IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID) {
		return IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID
	}
	return IDENTITY_PROVIDER_B2C
}

/** Raw GBPE / integrator string (e.g. `getIdentityProviderDataAttribute()`) resolves to Entra. */
export function isEntraExternalIdAttribute(value: string | null | undefined): boolean {
	return parseIdentityProviderAttribute(value) === IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID
}

/** Resolved {@link IdentityProvider} is Entra (after {@link parseIdentityProviderAttribute}). */
export function isEntraIdentityProvider(provider: IdentityProvider): boolean {
	return provider === IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID
}

const GLOBAL_NAV_SRC_MARKER = 'global-nav'

function isEntraEnabledQueryOnUrl(src: string): boolean {
	try {
		const url = new URL(src, window.location.origin)
		if (!url.searchParams.has('entra-enabled')) {
			return false
		}
		const v = (url.searchParams.get('entra-enabled') ?? '').toLowerCase()
		if (v === 'false' || v === '0' || v === 'no') {
			return false
		}
		return true
	} catch {
		return false
	}
}

/**
 * True when any loaded script's `src` looks like the global-nav bundle and includes `entra-enabled`
 * (e.g. `.../global-nav.chevrolet-interim-us-en.js?entra-enabled`). Handy for manual Entra testing without
 * `data-auth-identity-provider`. The data attribute still wins when present on the GBPE script.
 */
export function isEntraEnabledViaGlobalNavQuery(): boolean {
	if (typeof document === 'undefined') {
		return false
	}
	for (const el of document.querySelectorAll('script[src]')) {
		const raw = (el as HTMLScriptElement).getAttribute('src')
		if (!raw) {
			continue
		}
		const resolved = (el as HTMLScriptElement).src || new URL(raw, window.location.origin).href
		if (!resolved.toLowerCase().includes(GLOBAL_NAV_SRC_MARKER)) {
			continue
		}
		if (isEntraEnabledQueryOnUrl(resolved)) {
			return true
		}
	}
	return false
}

/**
 * Effective identity provider string from the GBPE script's `data-auth-identity-provider`, or from a
 * global-nav `?entra-enabled` testing flag when the attribute is unset. Reads `selfScriptEl` each call.
 */
export function getIdentityProviderDataAttribute(): string | null {
	if (selfScriptEl) {
		const fromAttr = selfScriptEl.getAttribute(ScriptDataAttributes.identityProvider)?.trim()
		if (fromAttr) {
			return fromAttr
		}
	}
	if (isEntraEnabledViaGlobalNavQuery()) {
		return IDENTITY_PROVIDER_ENTRA_EXTERNAL_ID
	}
	if (!selfScriptEl) {
		return null
	}
	return null
}
